import { FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorResponse, useNavigate, useParams } from 'react-router-dom';
import { useConfirm } from '../../Contexts/hooks';
import { useNotification } from '../../Contexts/NotificationContext';
import {
    ActionTypes,
    ApiException,
    AttachmentExtensionType,
    AttachmentSourceType,
    ConversationCreate,
    ConversationPatch,
    GptTypes,
    IAttachment,
    IConversation,
    IUserPreferences,
    UserPreferences,
} from '../../EnterpriseGPTApi/EnterpriseGPTApiV1Client';
import { performEnterpriseGPTApiCall } from '../../Hooks/useMiddlewareApiCall';
import { useConversationActions } from '../../Hooks/useConversationActions';
import { buildRoute, ChatRoute, ChatSpecificRoute } from '../../Routes';
import { logout } from '../../Security/azureAdAuthentication';
import { addConversationAction, removeConversationAction, updateActiveConversationAction } from '../../store/actions/conversationActions';
import { updateRemaingQuotaAction } from '../../store/actions/quotaActions';
import { setUserPreferencesAction } from '../../store/actions/userPreferencesAction';
import { useConversations, useUserPreferences } from '../../store/hooks';
import { AppConversation, AppPrompt } from '../../store/models/conversation';
import { getAttachmentExtensionTypeByFilename } from '../../Utils/file';
import { ConversationView } from './ConversationView';

// manages the whole conversation flow
const ConversationHandler: FC = () => {
    const { chats, activeChat, dispatch } = useConversations();
    const { userPreferences, dispatch: userPreferencesDispatch } = useUserPreferences();
    const { handleRemoveConversation, handleConversationTitleChange, handlePinToggle } = useConversationActions();
    const [canSubmitQuery, setCanSubmitQuery] = useState<boolean>(true);
    const [completionInProgress, setCompletionInProgress] = useState<boolean>(false);
    const [isUploadAttachmentOver, setIsUploadAttachmentOver] = useState<boolean>(false);
    const [restoreInfo, setRestoreInfo] = useState<{ trigger: number; reason?: 'error' | 'stop' }>({ trigger: 0, reason: undefined });
    const { t } = useTranslation();
    const confirm = useConfirm();
    const { dispatchNotification } = useNotification();
    const navigate = useNavigate();
    const [abortController, setAbortController] = useState<AbortController>(new AbortController());
    const { activeGpt } = useConversations();
    const { id: conversationId } = useParams();

    useEffect(() => {
        if (!conversationId) {
            // Navigate to the chat route without a specific conversation ID
            const startConversation = chats.find((c) => c.gpt?.type === GptTypes.SyGpt);
            if (startConversation?.id) {
                navigate(ChatRoute);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setRestoreInfo({ trigger: 0, reason: undefined });
    }, [activeChat?.id]);

    useEffect(() => {
        setRestoreInfo({ trigger: 0, reason: undefined });
    }, [activeChat?.id]);

    const handleConversationUpdate = (actionType: ActionTypes, updatedPromptIndex: number, data: IConversation) => {
        if (actionType === ActionTypes.RegenerateAnswer) {
            setCanSubmitQuery(false);
            setCompletionInProgress(true);
            if (activeChat) {
                const newPrompts = activeChat?.prompts?.slice(0, updatedPromptIndex) ?? [];
                const query = activeChat?.prompts?.[updatedPromptIndex]?.query;
                if (query) {
                    const chatFromLastUpdatedQuery = { id: activeChat.id, conversation: { ...activeChat, prompts: [...newPrompts, { query, queryOn: new Date(), plugins: [] }] } };
                    dispatch(updateActiveConversationAction(chatFromLastUpdatedQuery));
                }
            }
        }

        performEnterpriseGPTApiCall(async (client) => {
            try {
                // send the updated conversation (e.g. a new user message, a request to regenerate an answer or a user feedback) to the back-end
                const patch = new ConversationPatch({
                    ...data,
                    currentPromptFeedbackIndex: actionType === ActionTypes.Feedback ? updatedPromptIndex : undefined,
                    actionType,
                    llmDeploymentName: data.llm,
                });
                const updatedConversation = await client.patchConversation(data.id, patch);
                
                dispatch(updateActiveConversationAction({ id: updatedConversation.id, conversation: updatedConversation }));
            } catch (e) {
                if ((e as ErrorResponse).status === 429) {
                    dispatchNotification('conversation.serviceHighDemand', 'error');
                } else {
                    dispatchNotification('error.api.patch.conversation', 'error');
                }                
            } finally {
                setCanSubmitQuery(true);
                setCompletionInProgress(false);
            }
        });
    };

    const handleUpdateAnsweredQuery = (updatedPromptIndex: number, newQuery: string) => {
        const query = newQuery.trim();
        const newPrompts = activeChat?.prompts?.slice(0, updatedPromptIndex) ?? [];
        if (activeChat) {
            const chatFromLastUpdatedQuery = { id: activeChat.id, conversation: { ...activeChat, prompts: [...newPrompts, { query, queryOn: new Date(), plugins: [] }] } };
            dispatch(updateActiveConversationAction(chatFromLastUpdatedQuery));
            performEnterpriseGPTApiCall(async (client) => {
                try {
                    const patch = new ConversationPatch({ ...activeChat, prompts: newPrompts, currentQuery: query, actionType: ActionTypes.Message, llmDeploymentName: activeChat.llm });
                    const updatedConversation = await client.patchConversation(activeChat.id, patch);
                    dispatch(updateActiveConversationAction({ id: updatedConversation.id, conversation: updatedConversation }));
                    dispatch(updateRemaingQuotaAction({ limit: updatedConversation.userQuotaLimit, remaining: updatedConversation.userQuotaRemaining }));
                } catch (e) {
                    if ((e as Error).message !== 'canceled') {
                        dispatch(
                            updateActiveConversationAction({
                                id: activeChat.id,
                                conversation: { ...activeChat },
                            }),
                        );
                        dispatchNotification('error.api.patch.conversation', 'error');
                    }
                }
            });
        }
    };

    // sends the user query to the back-end
    const handleSendQuery = async (query: string, plugins: string[], modelName: string): Promise<boolean> => {
        setCanSubmitQuery(false);
        setCompletionInProgress(true);
        if (!activeChat) {
            // should not happen because if activechat is not defined we should not render that page
            setCanSubmitQuery(true);
            setCompletionInProgress(false);
            return false;
        }
        const newPrompt: AppPrompt = { query: query.trim(), queryOn: new Date(), answer: undefined, answerOn: undefined };

        const modifiedConversation: AppConversation = {
            ...activeChat,
            currentQuery: '',
            prompts: activeChat.prompts !== undefined ? [...activeChat.prompts, newPrompt] : [newPrompt],
        };

        dispatch(
            updateActiveConversationAction({
                id: modifiedConversation.id,
                conversation: modifiedConversation,
            }),
        );

        const sendSucceeded = await performEnterpriseGPTApiCall(async (client) => {
            let conversationId = modifiedConversation.id;
            // Check if this is a draft conversation that needs to be created in the backend
            if (modifiedConversation.id === 'DRAFT') {
                try {
                    const conversationCreate = new ConversationCreate({ gpt: { id: activeChat.gpt?.id, type: activeChat.gpt?.type } });
                    const newConversation = await client.createConversation(conversationCreate, abortController.signal);
                    conversationId = newConversation.id;
                    dispatch(updateActiveConversationAction({ id: modifiedConversation.id, conversation: { ...newConversation, ...modifiedConversation, id: newConversation.id } }));
                    // Navigate to the new conversation URL after creating it in the backend
                    navigate(buildRoute(ChatSpecificRoute, { id: newConversation.id }));
                } catch {
                    dispatchNotification('error.api.post.conversation', 'error');
                    dispatch(
                        updateActiveConversationAction({
                            id: modifiedConversation.id,
                            conversation: { ...modifiedConversation, prompts: modifiedConversation.prompts?.slice(0, -1) },
                        }),
                    );
                    return false;
                }
            }
            const model = modelName.split(' ');
            const modelEffort = model.length > 1 ? model[1] : '';

            const patch = new ConversationPatch({
                currentQuery: query.trim(),
                currentPlugins: plugins,
                prompts: modifiedConversation.prompts?.slice(0, -1),
                title: modifiedConversation.title,
                attachments: modifiedConversation.attachments,
                actionType: ActionTypes.Message,
                llmDeploymentName: model[0],
                reasoningLevel: modelEffort,
            });

            try {
                const updatedConversation = await client.patchConversation(conversationId, patch, abortController.signal);
                dispatch(updateActiveConversationAction({ id: updatedConversation.id, conversation: updatedConversation }));
                dispatch(updateRemaingQuotaAction({ limit: updatedConversation.userQuotaLimit, remaining: updatedConversation.userQuotaRemaining }));
                return true;
            } catch (e) {
                // handle different, custom error cases sent from the back-end
                if ((e as ErrorResponse).status === 401) {
                    dispatchNotification('error.api.common.expiredAuthentication', 'error', 'Login', logout);
                } else if ((e as ErrorResponse).status === 408) {
                    dispatchNotification('error.api.common.timeout', 'error');
                } else if ((e as ErrorResponse).status === 413) {
                    dispatchNotification('error.api.common.reachedTokenLimit', 'error');
                } else if ((e as ErrorResponse).status === 415) {
                    dispatchNotification(`${t(`conversation.attachments.fileNotSupported`)} ${activeGpt?.attachment?.extensions?.join(', ')}.`, 'error');
                } else if ((e as ErrorResponse).status === 422) {
                    dispatchNotification('error.api.common.fileEmpty', 'error');
                } else if ((e as ErrorResponse).status === 426) {
                    dispatchNotification('error.api.common.invalidClientVersion', 'info', 'Update', () => window.location.reload());
                } else if ((e as ErrorResponse).status === 428) {
                    dispatchNotification('error.api.common.reachedQuotaLimit', 'error');
                } else if ((e as ErrorResponse).status === 429) {
                    dispatchNotification('error.api.common.tooManyRequests', 'error');
                } else if ((e as ErrorResponse).status === 451) {
                    dispatchNotification('error.api.common.contentFilter', 'error');
                } else if ((e as ApiException).status === 500 && (e as ApiException).response === 'ContentLengthExceeded') {
                    dispatchNotification('error.api.common.reachedContentLengthLimit', 'error');
                } else if ((e as Error).message !== 'canceled') {
                    dispatchNotification('error.api.patch.conversation', 'error');
                }
                dispatch(updateActiveConversationAction({ id: modifiedConversation.id, conversation: { ...modifiedConversation, prompts: modifiedConversation.prompts?.slice(0, -1) } }));
                return false;
            } finally {
                setCanSubmitQuery(true);
                setCompletionInProgress(false);
            }
        });

        const succeeded = sendSucceeded ?? false;
        if (!succeeded) {
            setRestoreInfo((prev) => ({ trigger: prev.trigger + 1, reason: 'error' }));
        } else {
            setRestoreInfo((prev) => ({ ...prev, reason: undefined }));
        }

        return succeeded;
    };

    const handleStopCompletion = () => {
        abortController.abort('user requested to abort completion');
        setAbortController(new AbortController());
        setCanSubmitQuery(true);
        setCompletionInProgress(false);
        if (activeChat?.prompts) {
            const lastPrompt = activeChat.prompts[activeChat.prompts.length - 1];
            lastPrompt.aborted = true;
        }
        if (activeChat) {
            dispatch(updateActiveConversationAction({ id: activeChat.id, conversation: { ...activeChat } }));
            dispatchNotification('success.conversation.completion.aborted', 'success');
        }
        // Trigger restore of query when stop responding is clicked
        setRestoreInfo((prev) => ({ trigger: prev.trigger + 1, reason: 'stop' }));
    };

    const hasMixedTypes = (currentAttachments: IAttachment[], files: FileList): boolean => {
        const spreadsheetType = AttachmentExtensionType.Spreadsheet;
        // Build a table of existing document types
        const currentTypes = currentAttachments.map((attachment) => getAttachmentExtensionTypeByFilename(attachment.name));
        // Build a table of new file types
        const newTypes = Array.from(files).map((file) => getAttachmentExtensionTypeByFilename(file.name));

        // Check if a document is of type Spreadsheet
        const hasSpreadsheet = [...currentTypes, ...newTypes].some((type) => type === spreadsheetType);

        if (hasSpreadsheet) {
            // If a document is of type Spreadsheet, all documents must be of that type
            return [...currentTypes, ...newTypes].some((type) => type !== spreadsheetType);
        }
        // In all other cases, return false
        return false;
    };

    // all the logic to upload a document to the back-end is managed here
    const onUploadDocumentAdd = useCallback(
        (files: FileList, source: AttachmentSourceType) => {
            setIsUploadAttachmentOver(false);
            performEnterpriseGPTApiCall(async (client) => {
                if (activeChat) {
                    let conversationId = activeChat.id;
                    
                    // Check if this is a draft conversation that needs to be created in the backend
                    if (activeChat.id === 'DRAFT') {
                        try {
                            const conversationCreate = new ConversationCreate({ gpt: { id: activeChat.gpt?.id, type: activeChat.gpt?.type } });
                            const newConversation = await client.createConversation(conversationCreate, abortController.signal);
                            conversationId = newConversation.id;
                            // Preserve the currentQuery when converting DRAFT to a persisted conversation
                            const preservedQuery = activeChat.currentQuery || '';
                            dispatch(updateActiveConversationAction({ id: 'DRAFT', conversation: { ...newConversation, ...activeChat, id: newConversation.id, currentQuery: preservedQuery } }));
                            // Navigate to the new conversation URL after creating it in the backend
                            navigate(buildRoute(ChatSpecificRoute, { id: conversationId }));
                        } catch (e) {
                            console.error('Failed to create conversation:', e);
                            dispatchNotification('error.api.post.conversation', 'error');
                            setIsUploadAttachmentOver(true);
                            return;
                        }
                    }

                    const currentAttachments = activeChat?.attachments ?? [];

                    // Check if all files aren't mixed types 'SreapSheet' and the other types
                    // Show confirmation dialog if mixed types are detected
                    if (hasMixedTypes(currentAttachments, files)) {
                        // Return a Promise from the confirm dialog that rejects when canceled
                        await confirm({
                            show: true,
                            title: t(`application.dialog.warningTitle`),
                            message: t(`conversation.attachments.multipleUploadDifferentTypes`),
                            confirmText: t('Ok'),
                            catchOnCancel: true, // Make sure this is set to true
                        });
                        setIsUploadAttachmentOver(true);

                        return; // Stop the upload process if the dialog is confirmed
                    }

                    const newAttachments: IAttachment[] = [];
                    const failedUploads: string[] = [];

                    // Process all files in the FileList
                    for (let i = 0; i < files.length; i++) {
                        const file = files[i];
                        try {
                            // Use the raw filename for the file object but encode it for the API request
                            const fileName = file.name;
                            const encodedFileName = encodeURIComponent(fileName);

                            // Create a FileParameter object with the file data and filename
                            const fileParam = { data: file, fileName: encodedFileName };

                            await client.uploadAttachment(conversationId, fileParam);

                            const fileExtension = activeGpt?.attachment?.extensions?.find((ext) => fileName.toLocaleLowerCase().endsWith(ext.value!))?.value;
                            const fileSizeMB = file.size / 1000000;

                            // Store the raw filename in the attachment object
                            const attachment: IAttachment = {
                                name: fileName,
                                extension: fileExtension,
                                sizeMB: fileSizeMB,
                                sourceType: source,
                                extensionType: getAttachmentExtensionTypeByFilename(fileName),
                            };

                            // Collect the new attachment
                            newAttachments.push(attachment);
                        } catch (e) {
                            failedUploads.push(file.name);
                            console.error('Failed to upload file:', e);
                        }
                    }

                    // After processing all files, update the conversation with all new attachments
                    if (newAttachments.length > 0) {
                        const updateConversation = {
                            ...activeChat,
                            id: conversationId,
                            attachments: [...(activeChat.attachments || []), ...newAttachments],
                        };
                        dispatch(updateActiveConversationAction({ id: conversationId, conversation: updateConversation }));
                    }

                    // Show error notification if any uploads failed
                    if (failedUploads.length > 0) {
                        dispatchNotification('error.api.post.attachment.upload', 'error');
                    }
                    setIsUploadAttachmentOver(true);
                }
            });
        },
        [activeChat, activeGpt, dispatch, t, confirm, dispatchNotification, abortController.signal, navigate],
    );

    const handleUplodDocumentRemove = useCallback(
        (filename: string) => {
            performEnterpriseGPTApiCall(async (client) => {
                if (activeChat && activeChat.attachments) {
                    try {
                        // Find the attachment by filename
                        const attachmentToDelete = activeChat.attachments.find((a) => a.name === filename);

                        if (attachmentToDelete) {
                            const remainingAttachments = activeChat.attachments.filter((a) => a.name !== filename);
                            const hasNoMessages = !activeChat.prompts || activeChat.prompts.length === 0;
                            const isLastAttachment = remainingAttachments.length === 0;

                            // If it's the last attachment and there are no messages, delete the conversation
                            if (isLastAttachment && hasNoMessages && activeChat.id !== 'DRAFT') {
                                // Use the encoded filename for the API call
                                const encodedFilename = encodeURIComponent(filename);
                                await client.deleteAttachment(activeChat.id, encodedFilename);
                                
                                // Delete the conversation from the backend
                                await client.deleteConversation(activeChat.id);

                                // Remove from Redux store
                                dispatch(removeConversationAction(activeChat.id));

                                // Create a new DRAFT conversation with the same GPT type
                                // Preserve the currentQuery if the user has already typed something
                                const draftConversation: AppConversation = {
                                    id: 'DRAFT',
                                    gpt: activeChat.gpt,
                                    title: '',
                                    prompts: [],
                                    attachments: [],
                                    currentQuery: activeChat.currentQuery || '', // Preserve user's typed query
                                    startedOn: new Date(),
                                    userName: activeChat.userName,
                                    llm: activeChat.llm,
                                    draft: true,
                                };

                                // Add DRAFT conversation to the store
                                dispatch(addConversationAction(draftConversation));
                                
                                // Set it as active conversation
                                dispatch(updateActiveConversationAction({ id: 'DRAFT', conversation: draftConversation }));

                                // Navigate to the chat route
                                navigate(ChatRoute);
                            } else if (activeChat.id === 'DRAFT') {
                                // If already in DRAFT mode, just update locally without API call
                                const updateConversation = {
                                    ...activeChat,
                                    attachments: remainingAttachments,
                                };

                                dispatch(
                                    updateActiveConversationAction({
                                        id: activeChat.id,
                                        conversation: updateConversation,
                                    }),
                                );
                            } else {
                                // Normal case: just remove the attachment from a persisted conversation
                                // Use the encoded filename for the API call
                                const encodedFilename = encodeURIComponent(filename);
                                await client.deleteAttachment(activeChat.id, encodedFilename);

                                // Update local state after successful deletion
                                const updateConversation = {
                                    ...activeChat,
                                    attachments: remainingAttachments,
                                };

                                dispatch(
                                    updateActiveConversationAction({
                                        id: activeChat.id,
                                        conversation: updateConversation,
                                    }),
                                );
                            }
                        } else {
                            console.error(`Attachment with name ${filename} not found`);
                        }
                    } catch (e) {
                        dispatchNotification('error.api.post.attachment.remove', 'error');
                    }
                }
            });
        },
        [activeChat, dispatch, dispatchNotification, navigate],
    );

    const isPinned = (userPreferences as IUserPreferences)?.pinnedConversations?.includes(activeChat?.id ?? '') ?? false;

    return activeChat ? (
        <ConversationView
            isUploadAttachmentOver={isUploadAttachmentOver}
            canSubmit={canSubmitQuery}
            data={activeChat}
            onUpdate={(actionType, updatePromptIndex, data) => handleConversationUpdate(actionType, updatePromptIndex, data)}
            onSendQuery={(query, plugins, modelName) => handleSendQuery(query, plugins, modelName)}
            onUpdateAnsweredQuery={handleUpdateAnsweredQuery}
            onStopCompletion={handleStopCompletion}
            onDocumentUpload={onUploadDocumentAdd}
            onDocumentRemove={handleUplodDocumentRemove}
            completionInProgress={completionInProgress}
            restoreLastQueryTrigger={restoreInfo.trigger}
            restoreReason={restoreInfo.reason}
            onTitleChange={handleConversationTitleChange}
            onRemoveConversation={handleRemoveConversation}
            onPinToggle={handlePinToggle}
            isPinned={isPinned}
        />
    ) : null;
};

export default ConversationHandler;
